import styled from 'styled-components';
import {
  isPaymentCurrency,
  isValidPaymentAmount
} from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import { Button as LibButton, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { doesPriceSupportPayment } from '../../utils';
import { useColumn } from '../columns/column-context';
import { HORIZONTAL_PADDING, VERTICAL_PADDING } from '../constants';
import { useFormPopup } from '../form-popup';
import { useLayout } from '../layouts/layout-context';
import { getAlignment } from '../utils';
import { ColumnElementBase } from './column-element-base';
import { type ColumnElement } from './column-elements';

export function Button({ ...forwardingProps }: ColumnElement) {
  const { t } = useLingui();
  const errorUnavailable = t`We’re sorry, the payment page isn’t available right now. Please contact us for assistance.`;

  const { fields } = useSettings();
  const {
    id,
    price,
    buttonAction: actionType,
    buttonCaption: caption,
    buttonLink: link,
    buttonText: text
  } = useColumn();
  const { checkIfCheckoutError, setCheckoutError } = useLayout();

  const { openPopup } = useFormPopup();

  const handleClick = () => {
    const { rawValue: url, target } = link;
    if (actionType === 'openLink' && url) {
      window.open(url, target);

      return;
    }

    if (actionType === 'openForm' && fields) {
      const { currency, price: amount } = price;
      const isValidPrice =
        doesPriceSupportPayment(price) &&
        isPaymentCurrency(currency) &&
        isValidPaymentAmount({ amount, currency })[0];

      if (isValidPrice) {
        openPopup(id);
      } else {
        setCheckoutError(id);
      }

      return;
    }
  };

  return (
    <Container {...forwardingProps}>
      <StyledLibButton text={text} onClick={handleClick} />

      <Show when={!!caption}>
        <Caption dangerouslySetInnerHTML={{ __html: caption }} />
      </Show>

      <Show when={checkIfCheckoutError(id)}>
        <Error
          className={createReadableClass({
            component: 'button',
            element: 'error'
          })}
        >
          {errorUnavailable}
        </Error>
      </Show>
    </Container>
  );
}

const Container = styled(ColumnElementBase).attrs({
  className: createReadableClass({ component: 'button', element: 'container' })
})`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${({ $additionalTopPadding = 0 }) =>
      VERTICAL_PADDING + $additionalTopPadding}px
    ${HORIZONTAL_PADDING}px ${VERTICAL_PADDING}px;
  align-items: ${({
    theme: {
      button: { alignment, fullWidth }
    }
  }) => getAlignment(fullWidth ? 'center' : alignment)};
`;

const StyledLibButton = styled(LibButton).attrs(
  ({
    theme: {
      button: { captionColor: _captionColor, text: _text, ...buttonProps }
    }
  }) => ({
    ...buttonProps,
    className: createReadableClass({ component: 'button' })
  })
)``;

const Caption = styled(Typography).attrs(
  ({
    theme: {
      button: { captionColor }
    }
  }) => ({
    className: createReadableClass({
      component: 'button',
      element: 'caption'
    }),
    color: captionColor,
    textStyle: { fontSize: 11 }
  })
)`
  margin-top: 12px;
`;

const Error = styled(Typography).attrs(
  ({
    theme: {
      button: { captionColor }
    }
  }) => ({
    className: createReadableClass({
      component: 'button',
      element: 'error'
    }),
    color: captionColor,
    textStyle: { fontSize: 13, textAlign: 'center' }
  })
)`
  margin-top: 12px;
`;
